(() => {
  // ─── Onglets de compatibilité ──────────────────────────────────────────
  // Un seul groupe d'appareils visible à la fois, choisi par onglet.
  function initCompatTabs(root) {
    root.querySelectorAll('[data-compat-tabs]:not([data-tapn-compat-bound])').forEach((tabs) => {
      tabs.dataset.tapnCompatBound = 'true';
      const scope = tabs.closest('.tapn-compat');
      const buttons = [...tabs.querySelectorAll('[data-compat-tab]')];

      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          buttons.forEach((other) => {
            const active = other === button;
            other.classList.toggle('is-active', active);
            other.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          scope.querySelectorAll('[data-compat-panel]').forEach((panel) => {
            panel.classList.toggle('is-hidden', panel.id !== button.dataset.target);
          });
        });
      });
    });
  }

  // ─── Panier pleine page ────────────────────────────────────────────────
  // Les actions passent par l'Ajax Cart API puis rechargent la page : le
  // récapitulatif, le badge header et les recommandations restent synchronisés.
  function initCartPage(root) {
    root.querySelectorAll('[data-tapn-cart-page]:not([data-tapn-cart-page-bound])').forEach((page) => {
      page.dataset.tapnCartPageBound = 'true';
      const form = page.querySelector('[data-tapn-cart-form]');
      if (!form) return;
      let pending = false;

      const status = page.querySelector('[data-cart-status]');
      const discountInput = page.querySelector('[data-cart-discount-input]');
      const discountButton = page.querySelector('[data-cart-discount-apply]');
      const discountStatus = page.querySelector('[data-cart-discount-status]');
      const announce = (message) => {
        if (!status) return;
        status.textContent = message;
        status.hidden = !message;
      };
      const setControlsDisabled = (disabled) => {
        page.querySelectorAll('[data-cart-quantity-change], [data-cart-quantity-input], [data-cart-remove]').forEach((control) => {
          const input = control.matches('[data-cart-quantity-change]')
            ? page.querySelector(`[data-cart-quantity-input][data-cart-line="${CSS.escape(control.dataset.cartLine || '')}"]`)
            : null;
          const isMinimumQuantityControl = control.dataset.cartQuantityChange === '-1' && Number(input?.value || 0) <= 1;
          control.disabled = disabled || isMinimumQuantityControl;
        });
      };

      const changeLine = async (id, quantity) => {
        if (pending) return;
        pending = true;
        page.setAttribute('aria-busy', 'true');
        setControlsDisabled(true);
        announce('Mise à jour du panier…');
        try {
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id, quantity }),
          });
          if (!response.ok) throw new Error(`Cart change failed: ${response.status}`);
          window.location.reload();
        } catch {
          page.removeAttribute('aria-busy');
          pending = false;
          setControlsDisabled(false);
          announce('Impossible de mettre à jour le panier. Vérifiez votre connexion puis réessayez.');
        }
      };

      page.querySelectorAll('[data-cart-quantity-change]').forEach((button) => {
        button.addEventListener('click', () => {
          const input = page.querySelector(`[data-cart-quantity-input][data-cart-line="${CSS.escape(button.dataset.cartLine || '')}"]`);
          if (!input) return;
          const next = Math.max(0, Number(input.value || 0) + Number(button.dataset.cartQuantityChange || 0));
          changeLine(button.dataset.cartLine, next);
        });
      });

      page.querySelectorAll('[data-cart-remove]').forEach((button) => {
        button.addEventListener('click', () => changeLine(button.dataset.cartRemove, 0));
      });

      page.querySelectorAll('[data-cart-quantity-input]').forEach((input) => {
        input.addEventListener('change', () => changeLine(input.dataset.cartLine, Math.max(0, Number(input.value || 0))));
      });

      const applyDiscount = () => {
        const code = discountInput?.value.trim();
        if (!code) {
          if (discountStatus) {
            discountStatus.textContent = 'Saisissez un code promo.';
            discountStatus.hidden = false;
          }
          discountInput?.focus();
          return;
        }

        if (discountButton) discountButton.disabled = true;
        if (discountStatus) {
          discountStatus.textContent = 'Application du code promo…';
          discountStatus.hidden = false;
        }
        const base = page.dataset.discountBase || '/discount/';
        const redirect = page.dataset.cartRedirect || '/cart';
        window.location.assign(`${base}${encodeURIComponent(code)}?redirect=${encodeURIComponent(redirect)}`);
      };

      discountButton?.addEventListener('click', applyDiscount);
      discountInput?.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        applyDiscount();
      });
    });
  }

  // ─── Pages légales : sommaire + apparition au défilement ─────────────────
  // Le texte vient de l'éditeur riche Shopify, sans structure garantie : on
  // regroupe donc les enfants du contenu autour de chaque h2 au chargement,
  // plutôt que de dépendre d'un balisage produit par l'éditeur.
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function slugify(text) {
    return text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'section';
  }

  function initLegalPage(root) {
    root.querySelectorAll('[data-tapn-legal]:not([data-tapn-legal-bound])').forEach((page) => {
      page.dataset.tapnLegalBound = 'true';
      const prose = page.querySelector('.tapn-legal__prose');
      const toc = page.querySelector('[data-legal-toc]');
      if (!prose) return;

      const groups = [];
      [...prose.children].forEach((el) => {
        if (el.tagName === 'H2' || !groups.length) groups.push([]);
        groups[groups.length - 1].push(el);
      });
      if (!groups.length) return;

      const usedIds = new Set();
      const tocList = document.createElement('ul');
      tocList.className = 'tapn-legal__toc-list';
      const fragment = document.createDocumentFragment();

      groups.forEach((group) => {
        const section = document.createElement('section');
        section.className = 'tapn-legal__section tapn-reveal';
        group.forEach((el) => section.appendChild(el));
        fragment.appendChild(section);

        const heading = group.find((el) => el.tagName === 'H2');
        if (!heading) return;
        let id = slugify(heading.textContent);
        while (usedIds.has(id)) id += '-2';
        usedIds.add(id);
        heading.id = id;

        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${id}`;
        link.className = 'tapn-legal__toc-link';
        link.textContent = heading.textContent;
        item.appendChild(link);
        tocList.appendChild(item);
      });

      prose.replaceChildren(fragment);
      if (toc && tocList.children.length) {
        toc.appendChild(tocList);
        toc.hidden = false;
      }

      const reveals = [...prose.querySelectorAll('.tapn-reveal')];
      const show = (element) => element.classList.add('is-visible');
      if (prefersReducedMotion.matches || typeof IntersectionObserver === 'undefined') {
        reveals.forEach(show);
        return;
      }
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const observer = new IntersectionObserver((entries) => {
        entries.filter((entry) => entry.isIntersecting).forEach((entry) => {
          show(entry.target);
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
      reveals.forEach((element) => {
        if (element.getBoundingClientRect().top < viewportHeight * 0.92) show(element);
        else observer.observe(element);
      });
    });
  }

  // ─── Page test NFC ou QR Code : reveals de la template d'inspiration ─────
  function initNfcQrTest(root) {
    root.querySelectorAll('[data-tapn-nfc-qr-test]:not([data-tapn-nfc-qr-bound])').forEach((section) => {
      section.dataset.tapnNfcQrBound = 'true';
      const reveals = [...section.querySelectorAll('.tapn-nfc-qr-test__reveal')];
      section.dataset.tapnNfcQrReady = 'true';

      const show = (element) => element.classList.add('is-visible');
      if (prefersReducedMotion.matches || typeof IntersectionObserver === 'undefined') {
        reveals.forEach(show);
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.filter((entry) => entry.isIntersecting).forEach((entry) => {
          show(entry.target);
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

      reveals.forEach((element) => {
        element.style.transitionDelay = `${Number(element.dataset.delay || 0)}ms`;
        if (element.getBoundingClientRect().top < window.innerHeight * 0.92) show(element);
        else observer.observe(element);
      });
    });
  }

  function initAll(root = document) {
    initCompatTabs(root);
    initCartPage(root);
    initLegalPage(root);
    initNfcQrTest(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
